import { store } from "../../reduxStore";
import { splitLeaseParam } from "../leaseMode";

// GA4 event wrappers, mirroring reservation-machine's GoogleAnalyticsEvents.
// gtag.js itself is loaded statically in public/index.html (prod hostnames only).
// These wrappers gate on REACT_APP_CONTEXT === "production": in any other
// environment events are console-logged instead of sent, so the full funnel can be
// verified locally without touching GA.

export type GA4Params = Record<string, string | number | boolean | null | undefined | object>;

const appContext = (): string =>
  (process.env.REACT_APP_CONTEXT as string) || (window as any).appContext || "production";

const trackingEnabled = (): boolean => (window as any).gtagTracking !== false;

export const ga4Event = (
  eventName: string,
  params: GA4Params = {},
  opts?: { gtagTracking?: boolean; appContext?: string }
) => {
  const app = opts?.appContext ?? appContext();
  const enabled = opts?.gtagTracking ?? trackingEnabled();
  if (app !== "production" || !enabled) {
    console.log(`GA4 not sent [${app}, tracking=${enabled}] event=${eventName} params=${JSON.stringify(params)}`);
    return;
  }
  (window as any).gtag?.("event", eventName, params);
};

// The lease identification link carries a lease token in ?lease=. The token is a bearer for lease requests, so it
// must never reach GA4 or the console. src/leaseLink.ts strips it from the address before the app starts; this is the
// second line of defence for any path or URL that still has it.
export const redactLeaseParam = (pathOrUrl: string): string => {
  if (!pathOrUrl) return pathOrUrl;
  const hashAt = pathOrUrl.indexOf("#");
  const hash = hashAt === -1 ? "" : pathOrUrl.slice(hashAt);
  const beforeHash = hashAt === -1 ? pathOrUrl : pathOrUrl.slice(0, hashAt);
  const queryAt = beforeHash.indexOf("?");
  if (queryAt === -1) return pathOrUrl;
  const { rest, lease } = splitLeaseParam(beforeHash.slice(queryAt + 1));
  if (lease.length === 0) return pathOrUrl;
  return beforeHash.slice(0, queryAt) + (rest.length > 0 ? "?" + rest.join("&") : "") + hash;
};

export const sendVirtualPageView = (path: string, title?: string) => {
  const app = appContext();
  const params = {
    page_path: redactLeaseParam(path),
    page_title: title || document.title,
    page_location: redactLeaseParam(window.location.href),
  };
  if (app !== "production") {
    console.log(`%cGA4 not sent [${app}] event=virtual_pageview params=${JSON.stringify(params)}`, "color:#2196f3;font-weight:bold;");
    return;
  }
  (window as any).gtag?.("event", "virtual_pageview", params);
};

// Linkage defaults merged onto every event so the cross-app connection is never
// forgotten: reservation_id ties activity back to the www booking purchase
// (transaction_id = reservationId there), customer_id == user_id, and app keeps
// the shared property cleanly segmentable from www ("reservation_machine").
export const ga4Defaults = (): GA4Params => {
  const s = store.getState().chauffeurReducer;
  const out: GA4Params = { app: "chauffeur" };
  if (s?.reservation?.id != null) out.reservation_id = String(s.reservation.id);
  const cid = s?.reservation?.customerId ?? s?.user?.id;
  if (cid != null) out.customer_id = String(cid);
  out.launcher_mode = !!sessionStorage.getItem("L-M");
  return out;
};

export const track = (eventName: string, params: GA4Params = {}) =>
  ga4Event(eventName, { ...ga4Defaults(), ...params });
